import { MapPinCheck, CheckCircle2, XCircle, MapPin } from 'lucide-react';
import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { ApiCall, convertToNumber, } from '../CommonComponents/CustomHooks';
import { NotificationSound } from '../CommonComponents/CommonNotification';
import { AutoCompleteBoxNew, FormButton, InputBox } from '../CommonComponents/InputBox';
import { setSubLoading } from '../../Redux/Services/AuthSlice';
import { handleDownloadExcel } from '../CommonComponents/ExportData';
import { ColorRing } from 'react-loader-spinner';
import Pagination from '@mui/material/Pagination';

const PincodeServiceability = () => {
    const inputrefs = useRef([])
    const dispatch = useDispatch([])
    const [isLoading, setIsLoading] = useState(false);
    const [tableData, setTableData] = useState([])
    const [currentPageNo, setCurrentPageNo] = useState(1);
    const [formData, setFormData] = useState({
        vendorCode: "",
        sourcePincode: "",
        deliveryPincode: ""
    });
    const [sourcePincodeData, setSourcePincodeData] = useState({});
    const [deliveryPincodeData, setDeliveryPincodeData] = useState({});
    const [isAPICalled, setIsAPICalled] = useState(false);
    const sourcePincodeTimer = useRef(null);
    const deliveryPincodeTimer = useRef(null);

    const getPincodeServiceabilityData = async (CurrentPage) => {
        let PageIndex = 1
        if (CurrentPage !== undefined && typeof (CurrentPage) !== "object") {
            PageIndex = CurrentPage;
            setCurrentPageNo(CurrentPage);
        }
        else {
            setCurrentPageNo(1);
        }
        const data = {
            ...formData,
            Type: 'S',
            PageIndex: `${PageIndex}`,
        }
        setIsLoading(true)
        let res = await ApiCall(`/api/Settings/GetPincodeServiceability`, data)
        if (res.Status === 'SUCCESS') {
            setTableData(res);
        } else if (res.Status === 'ERROR') {
            await NotificationSound(res.Status, res.Message, res.Focus, inputrefs);
            setTableData([]);
        }
        setIsLoading(false)
        if (res.Status === 'EXPIRED') {
            await NotificationSound(res.Status, res.Message, res.Focus, inputrefs);
        }
    }

    const handlePageChange = (event, pagenumber) => {
        setCurrentPageNo(pagenumber);
    };

    const handleClear = () => {
        setFormData({
            vendorCode: "",
            sourcePincode: "",
            deliveryPincode: ""
        })
        setSourcePincodeData({});
        setDeliveryPincodeData({});
        setTableData({});
        inputrefs?.current['txtVendorCode']?.focus()
        setIsAPICalled(false);
    }

    const getStateAndCityByPincode = async (pinCode, type) => {
        if (!pinCode || pinCode.length < 6) {
            if (type === "source") {
                setSourcePincodeData({});
            } else {
                setDeliveryPincodeData({});
            }
            return;
        }

        const request = {
            Type: "GetCityStateByPincode",
            ContextKey: pinCode,
            ContextKey2: "",
            ContextKey3: "",
            ContextKey4: "",
            ContextKey5: "",
            ContextKey6: ""
        };

        try {
            const response = await ApiCall(
                "/api/AutoComplete/GetDataByType",
                request
            );

            const data = response?.data?.[0];

            if (
                (response?.status === "SUCCESS" ||
                    response?.Status === "SUCCESS") &&
                data
            ) {
                if (type === "source") {
                    setSourcePincodeData(data);
                } else {
                    setDeliveryPincodeData(data);
                }
            } else {
                if (type === "source") {
                    setSourcePincodeData({});
                } else {
                    setDeliveryPincodeData({});
                }
            }
        } catch (error) {
            console.error("Pincode API Error:", error);
            if (type === "source") {
                setSourcePincodeData({});
            } else {
                setDeliveryPincodeData({});
            }
        }
    };

    const hideColumns = tableData?.HideColumns || [];

    const columns =
        tableData?.Data?.length > 0
            ? Object.keys(tableData.Data[0]).filter(
                (key) => !hideColumns.includes(key)
            )
            : [];
    const countNo = Math.ceil((parseInt(tableData?.TotalRecord || 0) / parseInt(tableData?.PageSize || 10)));

    const debounceGetStateAndCity = (pinCode, type) => {
        const timerRef =
            type === "source"
                ? sourcePincodeTimer
                : deliveryPincodeTimer;

        clearTimeout(timerRef.current);

        timerRef.current = setTimeout(() => {
            getStateAndCityByPincode(pinCode, type);
        }, 500);
    };

    useEffect(() => {
        inputrefs?.current['txtVendorCode']?.focus()
        document.title = 'Pincode Serviceability'
        if (isAPICalled) {
            getPincodeServiceabilityData();
            setIsAPICalled(false);
        }
    }, [isAPICalled]);

    return (
        <>
            <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                        <MapPinCheck className="w-6 h-6 text-indigo-600" />
                        <h2 className="text-xl font-semibold text-gray-800">Pincode Serviceability</h2>
                    </div>
                </div>

                <div>
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                        <AutoCompleteBoxNew
                            divclassname='md:col-span-6 lg:col-span-3'
                            id="txtVendorCode"
                            label="Vendor"
                            placeholder="Enter Vendor"
                            inputrefs={inputrefs}
                            autocompleteType="GetVendor"
                            value={formData.vendorCode || ''}
                            onChange={async (event, newInputValue) => {
                                newInputValue = newInputValue || '';
                                if (event.type !== 'keydown') {
                                    setFormData({ ...formData, vendorCode: newInputValue });
                                }
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    inputrefs.current['txtSourcePincode']?.focus();
                                }
                            }}
                        />

                        <div className="md:col-span-6 lg:col-span-3">
                            <InputBox
                                required
                                name="txtSourcePincode"
                                id="txtSourcePincode"
                                inputrefs={inputrefs}
                                maxLength='10'
                                label={'Source Pincode'}
                                placeholder="Enter Source Pincode."
                                value={formData.sourcePincode}
                                onChange={(e) => {
                                    const val = convertToNumber(e.target.value);
                                    setFormData({ ...formData, sourcePincode: val });
                                    debounceGetStateAndCity(val, "source");
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        inputrefs?.current['txtDeliveryPincode']?.focus();
                                    }
                                }}
                            />
                            {sourcePincodeData?.StateName && (
                                <div className="mt-1 text-gray-500 text-[10px]">
                                    {sourcePincodeData.StateName},{sourcePincodeData.City}
                                </div>
                            )}
                        </div>

                        <div className="md:col-span-6 lg:col-span-3">
                            <InputBox
                                required
                                name="txtDelhiveryPincode"
                                id="txtDeliveryPincode"
                                inputrefs={inputrefs}
                                maxLength='10'
                                label={'Delivery Pincode'}
                                placeholder="Enter Delivery Pincode"
                                value={formData.deliveryPincode}
                                onChange={(e) => {
                                    const val = convertToNumber(e.target.value);
                                    setFormData({ ...formData, deliveryPincode: val });
                                    debounceGetStateAndCity(val, "delivery");
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        inputrefs?.current['txtbtnSub']?.focus();
                                    }
                                }}
                            />
                            {deliveryPincodeData?.StateName && (
                                <div className="mt-1 text-gray-500 text-[10px]">
                                    {deliveryPincodeData.StateName},{deliveryPincodeData.City}
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="mt-4">
                        <FormButton
                            id={'txtbtnSub'}
                            inputrefs={inputrefs}
                            handleSearch={() => setIsAPICalled(true)}
                            save={false}
                            search={true}
                            handleExport={async () => {
                                await dispatch(setSubLoading(true))
                                handleDownloadExcel(`/api/Settings/GetPincodeServiceability`, formData, 'Pincode Serviceability')
                                await dispatch(setSubLoading(false))
                            }}
                            handleClear={handleClear}
                        />
                    </div>
                </div>
            </div>

        
                {isLoading ? (
                    <div className="flex justify-center items-center py-8">
                        <ColorRing
                            visible={true}
                            height="80"
                            width="80"
                            ariaLabel="blocks-loading"
                            wrapperStyle={{}}
                            wrapperClass="blocks-wrapper"
                            colors={['#e15b64', '#f47e60', '#f8b26a', '#abbd81', '#849b87']}
                        />
                    </div>
                ) : (
                    <>
                        {tableData?.Data?.length > 0 ? (
                            <div className="mt-5 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                                {/* Header */}
                                <div className="flex items-center justify-between border-b border-slate-200 bg-gradient-to-r from-slate-50 to-white px-5 py-3">
                                    <div>
                                        <h3 className="text-sm font-semibold text-slate-800">Pincode Serviceability</h3>
                                        <p className="mt-0.5 text-[11px] text-slate-500">Vendor service availability details</p>
                                    </div>
                                    <div className="flex items-center gap-2 rounded-full bg-indigo-50 px-3 py-1.5">
                                        <span className="h-2 w-2 rounded-full bg-indigo-500"></span>
                                        <span className="text-xs font-semibold text-indigo-700">{tableData?.TotalRecord || 0} Records</span>
                                    </div>
                                </div>

                                {/* Table */}
                                <div className="overflow-x-auto">
                                    <table className="w-full min-w-[900px] border-collapse">
                                        <thead>
                                            <tr className="bg-[var(--theme-color)]">
                                                {columns.map((column) => (
                                                    <th key={column} className="whitespace-nowrap border-r border-white/10 px-5 py-3.5 text-center text-[11px] font-bold uppercase tracking-wider text-white last:border-r-0">
                                                        {column}
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100">
                                            {tableData.Data.map((row, rowIndex) => (
                                                <tr key={rowIndex} className="group bg-white transition-all duration-200 hover:bg-indigo-50/40">
                                                    {columns.map((column) => {
                                                        const value = row[column];
                                                        const isYesNo = typeof value === "string" && ["YES", "NO"].includes(value.toUpperCase());
                                                        const isYes = isYesNo && value.toUpperCase() === "YES";

                                                        return (
                                                            <td key={column} className="whitespace-nowrap px-5 py-3 text-center text-xs text-slate-700">
                                                                {isYesNo ? (
                                                                    <span className={`inline-flex min-w-[70px] items-center justify-center gap-1.5 rounded-full px-3 py-1.5 text-[10px] font-bold tracking-wide transition-all duration-200 ${isYes ? "border border-emerald-200 bg-emerald-50 text-emerald-700 group-hover:bg-emerald-100" : "border border-rose-200 bg-rose-50 text-rose-700 group-hover:bg-rose-100"}`}>
                                                                        <i className={`fa ${isYes ? "fa-check-circle text-emerald-500" : "fa-times-circle text-rose-500"}`}></i>
                                                                        {value.toUpperCase()}
                                                                    </span>
                                                                ) : (
                                                                    <span className="font-medium text-slate-700 group-hover:text-indigo-700 transition-colors">
                                                                        {value || <span className="text-slate-300">—</span>}
                                                                    </span>
                                                                )}
                                                            </td>
                                                        );
                                                    })}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Footer */}
                                <div className="flex items-center justify-between border-t border-slate-100 bg-slate-50/70 px-5 py-2.5">
                                    <span className="text-[11px] text-slate-500">
                                        Showing <span className="font-semibold text-slate-700">{tableData.Data.length}</span> records
                                    </span>
                                    <span className="text-[11px] text-slate-400">Pincode Serviceability</span>
                                </div>
                            </div>
                        ) : (
                            <div className="my-5 flex flex-col items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-gradient-to-b from-slate-50 to-white px-6 py-12">
                                <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-indigo-50">
                                    <i className="fa fa-map-marker text-xl text-indigo-500"></i>
                                </div>
                                <p className="text-sm font-semibold text-slate-700">No Record Found</p>
                                <p className="mt-1 text-xs text-slate-400">Try changing your search criteria</p>
                            </div>
                        )}
                        {countNo > 1 && (
                            <div className="flex justify-end mt-4 overflow-x-auto whitespace-nowrap">
                                <Pagination
                                    count={countNo}
                                    variant="outlined"
                                    page={currentPageNo}
                                    onChange={handlePageChange}
                                    shape="rounded"
                                />
                            </div>
                        )}
                    </>
                )}
        </>
    )
}

export default PincodeServiceability;